import './../../Player.css';
import React, {useContext, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerContext } from '../../PlayerContext';
import { openFullscreen, browserLocales, unescapeHtml } from '../../Utils';
import { analyticsManager } from '../../../index';
import SecondaryButton, {influencerType} from '../components/SecondaryButton';
import FadeInPanel from '../components/FadeInPanel';
import CircularVideoAvatar from '../components/CircularVideoAvatar';
import { useInfluencerAvatarMedia } from '../hooks/useInfluencerAvatarMedia';
import { useAnimate } from 'framer-motion';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getStoredPixelConsent } from '../../PixelConsentOverlay';
import { hasChainRestaurants } from '../../chainRestaurantUtils';

function InfluencerButton(props){
    const { t } = useTranslation();
    const [isOpened, setIsOpened] = useState(false);

    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const propagateFocusChange = useContext(PlayerContext).get("propagateFocusChange");
    const pauseVideo = useContext(PlayerContext).get("pauseVideo");
    const setActiveFeature = useContext(PlayerContext).get("setActiveFeature");
    const activeFeature = useContext(PlayerContext).get("activeFeature");
    const appAnalyticsManager = useContext(PlayerContext).get("appAnalyticsManager");
    const playMode = useContext(PlayerContext).get("playMode");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const setPlayAudio = useContext(PlayerContext).get("setPlayAudio");
    const audioAlreadyDisabled = useContext(PlayerContext).get("audioAlreadyDisabled");
    const playAudio = useContext(PlayerContext).get("playAudio");
    const playerOnDesktop = useContext(PlayerContext).get("playerOnDesktop");
    const fromMaker = useContext(PlayerContext).get("fromMaker");
    const fromDemo = useContext(PlayerContext).get("fromDemo");
    const highlight = useContext(PlayerContext).get("highlight");
    const idPrefix = fromDemo ? (fromDemo +"-") : "";

    const influencerAvatarImage = String(props.story?.influencerAvatar || "").trim();
    const influencerVideoUrl = String(props.story?.influencerAvatarVideo || "").trim();
    const dialogOpen = isOpened && activeFeature === "influencer";
    const avatarMedia = useInfluencerAvatarMedia({
        enabled: dialogOpen,
        videoUrl: influencerVideoUrl,
        imageUrl: influencerAvatarImage,
    });

    const [, animate] = useAnimate();

    useEffect(() => {
        if (highlight != null){

            let elementId = null;
            
            if (highlight.includes("influencer")){
                elementId = "secondary-btn-influencer";
            }            

            const element = document.getElementById(elementId);
            if (elementId != null && element != null){                   
                const animation = async () => {
                    await animate(element, { background: "#FFFFFFFF"}, {duration: 0.6});
                    if (element != null)
                        animate(element, { background: "#FFFFFF00"}, {duration: 0.6});
                }
                
                animation();
            }
        }
      }, [highlight, animate]);

    if (props.story.posId == null && !hasChainRestaurants(props.story)) return (null);
        
    const open = () => {          
        if (!fromMaker /*&& !fromDemo*/ && !playerOnDesktop && !audioAlreadyDisabled && !playAudio){                        
            const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
            if (ambiantPlayer && ambiantPlayer.paused){
                try {
                    ambiantPlayer.play();                
                } catch(error){}
            }
            setPlayAudio(true);            
        }
        pauseVideo();
        openFullscreen();
        setActiveFeature("influencer");
        setIsOpened(true);
      
        analyticsManager.logCheckInfluencerTag(props.storyId, playMode, true);
                
        if (appAnalyticsManager)
            appAnalyticsManager.clickedInfluencers();

        if (getStoredPixelConsent() === 'accepted') {
            if (window.ttq){
                window.ttq.track('ViewContent', {                
                    "contents": [
                        {              
                            "content_id": "0",           
                            "content_name": "influencer",
                        }
                    ],
                });
            }
            if (window.fbq){                    
                window.fbq('track', 'ViewContent', {"content_name": "influencer"});
            }
        }
    };

    const close = () => {        
        propagateFocusChange(true);
        setIsOpened(false);
        setActiveFeature(null);
        analyticsManager.logCheckInfluencerTag(props.storyId, playMode, false);
    };   
    
    function showCloseButton(){
        return (
            <div style={{
                position: 'absolute',
                bottom: 16 * sizeMultiplier,
                left: 0,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 40
            }}>
                
                <IconButton
                    onClick={close}
                    style={{
                        background: "#fff",
                        borderRadius: "50%",                                    
                        padding: 4 * sizeMultiplier,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}                        
                >                                     
                    <CloseIcon style={{ color: "#808080", fontSize: 24 * sizeMultiplier, width: 28 * sizeMultiplier, height: 28 * sizeMultiplier,}} />                        
                </IconButton>
            </div>
        );                    
    }
    
    if (props.show){                  
        if (dialogOpen){             

            // Détecter la langue actuelle
            const language = browserLocales(true)[0] === "fr" ? "fr" : "en";
            
            // Récupérer les données depuis props.story
            const influencerTitleRaw = props.story.influencerTitle;
            const influencerDescriptionRaw = props.story.influencerDescription;
            const influencerPopupMedia = avatarMedia.showVideoAvatar
                ? influencerVideoUrl
                : influencerAvatarImage;
            
            // Gérer les traductions si les champs sont des objets
            let influencerTitle = typeof influencerTitleRaw === 'object' 
                ? (influencerTitleRaw[language] || influencerTitleRaw.fr || influencerTitleRaw.en || "")
                : (influencerTitleRaw || "");
            
            let influencerDescription = typeof influencerDescriptionRaw === 'object'
                ? (influencerDescriptionRaw[language] || influencerDescriptionRaw.fr || influencerDescriptionRaw.en || "")
                : (influencerDescriptionRaw || "");
            
            // Déséchapper le HTML échappé depuis la base de données
            influencerTitle = unescapeHtml(influencerTitle);
            influencerDescription = unescapeHtml(influencerDescription);
            
            // Nettoyer le HTML pour éviter les scripts malveillants
            const sanitizeHtml = (html) => {
                if (!html) return "";
                return html
                    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                    .replace(/\n/g, '<br>')
                    .replace(/(<br\s*\/?>\s*)+$/gi, '');
            };
            
            influencerTitle = sanitizeHtml(influencerTitle);
            influencerDescription = sanitizeHtml(influencerDescription);
            
            // Concaténer le titre avec la traduction de InfluencerButton
            const influencerButtonText = t('InfluencerButton');
            if (influencerTitle) {
                influencerTitle = influencerTitle + " " + influencerButtonText;
            } else {
                influencerTitle = influencerButtonText;
            }

            const bandHeight = 30 * sizeMultiplier;
            const avatarSize = 3 * bandHeight;
            const cardRadius = 8 * sizeMultiplier;

            const panelRootStyle = {
                position: 'absolute',
                top: 0,
                left: 0,
                width: storyDimensions.containerWidth,
                height: storyDimensions.containerHeight,
                pointerEvents: 'auto',
                zIndex: 20,
            };

            const renderOverlay = () => (
                <div
                    id={idPrefix + "gallery-overlay"}
                    className="BlockingOverlay"
                    style={{
                        width: storyDimensions.containerWidth,
                        height: storyDimensions.containerHeight,
                        background: "#FFFFFFD0",
                        pointerEvents: 'auto',
                    }}
                    onClick={close}
                />
            );

            const renderCard = () => (
                <div style={{
                    zIndex: 10,
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: 16 * sizeMultiplier,
                    paddingRight: 16 * sizeMultiplier,
                    pointerEvents: 'none',
                }}>
                    <div style={{
                        pointerEvents: 'auto',
                        width: '100%',
                        backgroundColor: '#FFFFFF',
                        borderRadius: cardRadius,
                        paddingBottom: 16 * sizeMultiplier,
                        maxHeight: storyDimensions.containerHeight * 0.7,
                        overflow: 'visible',
                        display: 'flex',
                        flexDirection: 'column',
                    }} onClick={(e)=>{e.stopPropagation();}}>
                        <div style={{
                            position: 'relative',
                            height: bandHeight,
                            backgroundColor: props.story.primaryColor,
                            borderTopLeftRadius: cardRadius,
                            borderTopRightRadius: cardRadius,
                            flexShrink: 0,
                        }}>
                            {influencerPopupMedia && (
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: 0,
                                    marginLeft: -avatarSize / 2,
                                    marginTop: -avatarSize / 2,
                                    zIndex: 2,
                                }}>
                                    {avatarMedia.showVideoAvatar ? (
                                        <CircularVideoAvatar
                                            videoRef={avatarMedia.videoRef}
                                            src={influencerVideoUrl}
                                            size={avatarSize}
                                            onVideoReady={avatarMedia.onVideoReady}
                                            onVideoError={avatarMedia.onVideoError}
                                        />
                                    ) : (
                                        <img
                                            src={influencerPopupMedia}
                                            alt={influencerTitle}
                                            style={{
                                                width: avatarSize,
                                                height: avatarSize,
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                display: 'block',
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{
                            overflowY: 'auto',
                            flex: 1,
                            minHeight: 0,
                        }}>
                            <div style={{
                                paddingLeft: 16 * sizeMultiplier,
                                paddingRight: 16 * sizeMultiplier,
                                paddingTop: influencerPopupMedia
                                    ? avatarSize / 2 - bandHeight + 16 * sizeMultiplier
                                    : 16 * sizeMultiplier,
                            }}>
                                {influencerTitle && (
                                    <div
                                        className="card-html-content"
                                        style={{
                                            marginBottom: 16 * sizeMultiplier,
                                            fontSize: 18 * sizeMultiplier,
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            textAlign: 'center',
                                            fontFamily: 'Roboto, sans-serif',
                                            lineHeight: 1.3,
                                        }}
                                        dangerouslySetInnerHTML={{__html: influencerTitle}}
                                    />
                                )}

                                {influencerDescription && (
                                    <div
                                        className="card-html-content"
                                        style={{
                                            paddingTop: 8 * sizeMultiplier,
                                            paddingBottom: 8 * sizeMultiplier,
                                            fontSize: 14 * sizeMultiplier,
                                            fontWeight: 'normal',
                                            color: '#666666',
                                            lineHeight: 1.4,
                                            fontFamily: 'Roboto, sans-serif',
                                            textAlign: 'left',
                                        }}
                                        dangerouslySetInnerHTML={{__html: influencerDescription}}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );

            if (avatarMedia.hasVideoAvatar) {
                return (
                    <div style={panelRootStyle} onClick={(e) => e.stopPropagation()}>
                        {renderOverlay()}
                        <FadeInPanel
                            className="influencer-container"
                            animationKey={avatarMedia.fadeAnimationKey}
                            active={avatarMedia.contentReady}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: avatarMedia.contentReady ? 'auto' : 'none',
                                zIndex: 10,
                            }}
                        >
                            {renderCard()}
                            {avatarMedia.contentReady && showCloseButton()}
                        </FadeInPanel>
                    </div>
                );
            }

            return (
                <FadeInPanel
                    className="influencer-container"
                    animationKey="influencer"
                    style={panelRootStyle}
                    onClick={(e) => e.stopPropagation()}
                >
                    {renderOverlay()}
                    {renderCard()}
                    {showCloseButton()}
                </FadeInPanel>
            );
        }
        else {                
            if (props.story.influencerTitle != null && props.story.influencerDescription != null && props.story.influencerAvatar != null){                           
                return (
                    <SecondaryButton story={props.story} buttonType={influencerType} open={open}/>
                );
            }
            else {                
                return null;
            }
        }                
    }
    else {        
        return null;
    }
}    

export default InfluencerButton;
